import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TemplateFrame } from '../../site/TemplateFrame';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/useAuth';
import { FormNotice } from './MemberForm';
import { messageFrom } from './memberErrors';

/**
 * Where the link in a confirmation email lands.
 *
 * The page posts the token rather than the link confirming on arrival, so a mail
 * provider's link scanner that fetches every URL in an email cannot confirm the
 * address before the member has seen it. It confirms as soon as it loads, since
 * the member has already clicked once and a second button would only be in the
 * way.
 *
 * The SecretsFL API used to send these, with a link to its own host. It stopped
 * at 3.0.0; this site sends its own now, and this is where they come back to.
 */

type State =
  | { kind: 'working' }
  | { kind: 'confirmed' }
  | { kind: 'already' }
  | { kind: 'failed'; message: string };

export function ConfirmEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, member, refreshMember } = useAuth();
  const signedIn = status === 'authenticated' && member !== null;

  const [state, setState] = useState<State>(() =>
    token
      ? { kind: 'working' }
      : {
          kind: 'failed',
          message:
            'That link is missing its code. Open it straight from the email, or sign in and send yourself a new one.',
        },
  );
  // Effects run twice in development. The token is not single-use in a way a
  // second post would break, but one request per visit is the honest count.
  const posted = useRef(false);

  useEffect(() => {
    if (!token || posted.current) return;
    posted.current = true;
    api.member
      .confirmEmail(token)
      .then((result) => setState({ kind: result.status === 'confirmed' ? 'confirmed' : 'already' }))
      .catch((err: unknown) =>
        setState({
          kind: 'failed',
          message: messageFrom(err, 'We could not confirm your email. Please try the link again.'),
        }),
      );
  }, [token]);

  // A signed-in member here is often the same person, in the same browser, and
  // their account page should stop asking them to confirm. Best effort: if it
  // fails, the next page load reads it fresh anyway.
  const confirmed = state.kind === 'confirmed' || state.kind === 'already';
  const refreshed = useRef(false);
  useEffect(() => {
    if (!confirmed || !signedIn || refreshed.current) return;
    refreshed.current = true;
    void refreshMember().catch(() => undefined);
  }, [confirmed, signedIn, refreshMember]);

  const next = signedIn ? (
    <Link className="button button-primary" to="/account">
      Go to your account
    </Link>
  ) : (
    <Link className="button button-primary" to="/login">
      Sign in
    </Link>
  );

  return (
    <TemplateFrame title="Confirm Your Email">
      <div className="row justify-content-center">
        <div className="col-md-10 col-lg-6">
          <div className="member-card">
            {state.kind === 'working' && (
              <>
                <h3>Confirming your email…</h3>
                <p className="po-muted">This only takes a moment.</p>
              </>
            )}

            {state.kind === 'confirmed' && (
              <>
                <h3>Email confirmed</h3>
                <p className="po-muted">
                  Thanks. Your email address is confirmed for your Secrets Hideaway account.
                </p>
                {next}
              </>
            )}

            {state.kind === 'already' && (
              <>
                <h3>Already confirmed</h3>
                <p className="po-muted">
                  Your email address was already confirmed. There is nothing more to do.
                </p>
                {next}
              </>
            )}

            {state.kind === 'failed' && (
              <>
                <h3>We could not confirm your email</h3>
                <FormNotice kind="error">{state.message}</FormNotice>
                {signedIn ? (
                  <Link className="button button-primary" to="/account">
                    Send a new link
                  </Link>
                ) : (
                  next
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </TemplateFrame>
  );
}
